import {tokens} from '@pleo-io/telescope'
import {DisclosureButton} from '@reach/disclosure'
import {motion} from 'framer-motion'
import {RemoveScroll} from 'react-remove-scroll'
import styled, {css} from 'styled-components'

import {Chevron} from '@/images/icons/chevron'
import {StyledButton} from '@helios/shared/components/button/button'
import {BackgroundWrapper, container} from '@helios/shared/styles/grid'
import {text, StyledText} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'
import {animations, colors, headerSizes, zIndex, fonts} from '@helios/shared/theme'
import mq from '@helios/shared/utils/media-query'

interface WrapperProps {
    $menuOpen?: boolean
}

export const mobileMenuBreakpoint = 'sm'
export const tabletMenuBreakpoint = 'md'
const mobileMenuWidth = '480px'

const linksFocusStyles = css`
    :focus {
        ${focusStyles()}
        border-radius: ${tokens.spacing4};
    }
`

const textLinkStyle = css`
    padding: 0;
    ${text({size: 'md', font: 'primary'})}

    ${mq.min(tabletMenuBreakpoint)} {
        ${text({size: 'xs', font: 'secondary'})}
    }
`

const noUnderline = css`
    &,
    :hover,
    :focus,
    :active {
        text-decoration: none;
    }
`

export const NavCta = styled(StyledButton)`
    font-size: ${tokens.fontLarge};
    transition: background ${tokens.fastIn};
`

const Logo = styled.div`
    * {
        display: block;
        height: 100%;
    }

    img {
        object-fit: contain;
    }

    a {
        ${linksFocusStyles}
    }
`

export const MobileLogo = styled(Logo)`
    display: none;
    width: 72px;
    padding: ${tokens.spacing10} 0; /* Increases height to match height when sticky CTA shows */
`

export const DesktopLogo = styled(Logo)`
    z-index: ${zIndex.headerLogo};
    display: block;
    width: 54px;
`

export const LinkArrow = styled.span`
    ${mq.max(tabletMenuBreakpoint)} {
        display: none;
    }
`

export const BetaTag = styled.span`
    display: none;

    ${mq.min(tabletMenuBreakpoint)} {
        display: inline-block;
        ${text({size: 'xxs', font: 'secondary', weight: 'semiBold'})};
        padding: ${tokens.spacing4} ${tokens.spacing8};
        margin-left: ${tokens.spacing8};
        word-break: initial;
        vertical-align: middle;
        background: ${tokens.pink300};
        border-radius: ${tokens.arc4};
    }
`

export const Overlay = styled(motion.div)<{mobileMenuOpen: boolean; dropdownOpen: boolean}>`
    position: fixed;
    z-index: ${zIndex.menuOverlay};
    width: 100%;
    height: 100vh;
    background: ${tokens.shade900lighten20};
    opacity: 0;

    ${mq.min(mobileMenuBreakpoint)} {
        opacity: 0.2;
    }

    ${mq.min(tabletMenuBreakpoint)} {
        display: ${(props) => props.mobileMenuOpen && !props.dropdownOpen && 'none'};
    }
`

export const MobileMenuBtn = styled.button`
    display: none;
    padding: 0;
    cursor: pointer;
    background: none;
    border: none;

    img {
        display: block;
        width: auto;
        height: 28px;
    }
`

export const DisclosureWrapper = styled.div`
    display: grid;
    width: 100%;
    gap: ${tokens.spacing20};

    ${mq.min(tabletMenuBreakpoint)} {
        gap: unset;
    }
`

export const MenuBtnWithDropdown = styled(DisclosureButton)`
    ${textLinkStyle}
    background: none;
    border: none;

    ${mq.min(tabletMenuBreakpoint)} {
        ${text({size: 'xs', font: 'secondary'})}

        padding: 0 ${tokens.spacing16};
        margin-bottom: 0;
        cursor: pointer;
        transition: color ${tokens.smoothInOut};
    }

    ${DisclosureWrapper} & {
        ${mq.min(tabletMenuBreakpoint)} {
            padding: 0 ${tokens.spacing12};
        }

        ${mq.min('lg')} {
            padding: 0 ${tokens.spacing16};
        }
    }

    ${linksFocusStyles}
`

const NavGroup = styled.div`
    display: flex;
    align-items: center;
    ${text({size: 'sm', font: 'primary'})}

    /* main nav but not child */
    & > a:not(${NavCta}) {
        ${mq.min(tabletMenuBreakpoint)} {
            padding-right: ${tokens.spacing12};
            padding-left: ${tokens.spacing12};
            margin-left: 0;
            text-align: center;
        }

        ${mq.min('lg')} {
            padding-right: ${tokens.spacing16};
            padding-left: ${tokens.spacing16};
        }
    }
`

export const NavGroupLeft = styled(NavGroup)`
    display: grid;

    ${mq.min(tabletMenuBreakpoint)} {
        grid-template-columns: repeat(4, auto);
        padding-left: ${tokens.spacing20};
    }

    ${mq.min('lg')} {
        padding-left: ${tokens.spacing24};
    }
`

export const NavGroupRight = styled(NavGroup)<{$menuOpen: boolean}>`
    ${mq.max(tabletMenuBreakpoint)} {
        display: grid;
        gap: ${tokens.spacing20};
        grid-template-rows: auto 1fr;
        grid-template-columns: 1fr;
        height: 100%;
        padding-bottom: 160px;
    }

    ${mq.min(tabletMenuBreakpoint)} {
        align-items: center;
    }
`

export const NavLink = styled.a<{$isHighlighted?: boolean}>`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: ${tokens.spacing8};
    ${noUnderline}
    ${textLinkStyle}

    ${mq.min(tabletMenuBreakpoint)} {
        gap: ${tokens.spacing4};
        ${text({size: 'xs', font: 'secondary'})}

        ${NavGroupRight} & {
            height: 100%;
        }

        ${NavGroup}:hover & {
            color: ${tokens.shade600};
            transition: color ${tokens.smoothInOut};

            :hover {
                color: ${tokens.shade900};
            }
        }

        /* we need to win the specificity war with the hover styles below, 
        since the link should stay highlighted, even when the hover is gone */
        &&& {
            color: ${(props) => (props.$isHighlighted ? tokens.shade900 : undefined)};
        }

        ${linksFocusStyles}
    }

    ${mq.min('lg')} {
        gap: ${tokens.spacing8};
    }
`

export const MenuBadge = styled.span`
    position: relative;
    top: -4px;
    width: 6px;
    height: 6px;
    background: ${tokens.pink700};
    border-radius: ${tokens.circle};
`

export const Arrow = styled(Chevron)<{$isExpanded: boolean}>`
    width: ${tokens.spacing16};
    height: ${tokens.spacing16};
    transition: transform ${tokens.smoothInOut};
    transform: ${(props) => props.$isExpanded && 'rotate(-180deg)'};

    ${mq.min(mobileMenuBreakpoint)} {
        width: ${tokens.spacing12};
        height: ${tokens.spacing12};
    }
`

export const DropdownWrapper = styled.div`
    left: 0;
    width: 100%;

    ${mq.min(tabletMenuBreakpoint)} {
        position: absolute;
        padding-top: ${tokens.spacing12};
    }
`

export const DropdownInner = styled.div`
    display: grid;
    ${container}
    padding-bottom: ${tokens.spacing12};
    background-color: ${tokens.shade000};

    ${mq.min(tabletMenuBreakpoint)} {
        padding-top: ${tokens.spacing60};
        padding-bottom: ${tokens.spacing60};
        justify-content: center;
        gap: ${tokens.spacing16};
        grid-template-columns: 1fr 3fr;
    }

    ${mq.min('md')} {
        padding-top: ${tokens.spacing40};
        padding-bottom: ${tokens.spacing20};
        gap: ${tokens.spacing60};
        grid-template-columns: 1fr 3fr;
    }

    ${mq.min('lg')} {
        gap: 144px;
    }

    /* WhyPleo dropdown */
    ${DisclosureWrapper}:nth-child(2) & {
        ${mq.max(tabletMenuBreakpoint)} {
            gap: ${tokens.spacing20};
        }
    }
`

export const Dropdown = styled.div`
    display: grid;
    padding-bottom: ${tokens.spacing12};
    background-color: ${tokens.shade000};
    border-bottom-right-radius: ${tokens.spacing48};

    ${mq.min(tabletMenuBreakpoint)} {
        justify-content: center;
        padding-bottom: ${tokens.spacing40};
    }
`

const nestedDropdownClosed = css`
    height: 0;
    overflow: hidden;
    opacity: 0;
`

const nestedDropdownOpen = css`
    overflow: visible;
    opacity: 1;
`

export const NestedDropdown = styled.div<{$isExpanded: boolean}>(({$isExpanded}) => {
    return css`
        display: grid;
        transition: opacity ${tokens.fastInOut};

        ${mq.max(tabletMenuBreakpoint)} {
            padding-left: ${tokens.spacing20};
            gap: ${tokens.spacing20};
            ${$isExpanded ? nestedDropdownOpen : nestedDropdownClosed}
        }

        ${mq.min(tabletMenuBreakpoint)} {
            grid-template-columns: 1fr 1fr;
            grid-auto-rows: min-content;
            ${nestedDropdownOpen}
        }
    `
})

export const DropdownTitleArea = styled.div`
    display: grid;
    gap: ${tokens.spacing24};
    align-content: start;
    order: 2;

    ${mq.min(tabletMenuBreakpoint)} {
        padding-top: ${tokens.spacing20};
        order: 1;
    }

    ${DisclosureWrapper}:nth-child(2) & {
        ${mq.max(tabletMenuBreakpoint)} {
            order: 1;
        }
    }
`

export const DropdownTitleAreaHeader = styled.div`
    display: none;

    ${mq.min(tabletMenuBreakpoint)} {
        display: block;
        display: grid;
        gap: ${tokens.spacing10};
        grid-template-rows: auto 1fr;
    }
`

export const DropdownTitleAreaLinks = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    padding-top: ${tokens.spacing20};

    ${mq.min(tabletMenuBreakpoint)} {
        gap: ${tokens.spacing10};
        padding-top: 0;
    }

    ${DisclosureWrapper}:nth-child(2) & {
        ${mq.max(tabletMenuBreakpoint)} {
            padding-top: 0;
            gap: ${tokens.spacing20};
        }
    }
`

export const DropdownTitleAreaLink = styled.a`
    ${text({size: 'sm', font: 'primary', color: 'muted'})}
    padding: 0;
    text-align: inherit;
    text-decoration: underline;
    cursor: pointer;
    background: ${colors.link.pink.background};
    border: none;
    transition: background ${tokens.fastIn};

    :hover,
    :active {
        color: ${colors.link.pink.hover.foreground};
        background: ${colors.link.pink.hover.background};
    }

    ${linksFocusStyles}

    ${mq.max(tabletMenuBreakpoint)} {
        ${noUnderline}

        :hover {
            background: none;
        }
    }

    ${mq.min(tabletMenuBreakpoint)} {
        width: fit-content;
        ${text({size: 'xs', font: 'secondary', color: 'muted'})}
        color: ${colors.link.pink.foreground};
    }
`

export const DropdownContentAreaItem = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    padding: ${tokens.spacing20};
    border-radius: ${tokens.arc8};
    transition: background-color ${tokens.fastIn};

    ${mq.between(tabletMenuBreakpoint, 'lg')} {
        padding: ${tokens.spacing20} ${tokens.spacing10};
    }

    ${mq.max(tabletMenuBreakpoint)} {
        ${textLinkStyle}
        color: ${tokens.shade700};

        img {
            display: none;
        }
    }

    ${mq.min(tabletMenuBreakpoint)} {
        grid-template-columns: auto minmax(0, 1fr);

        img {
            width: ${tokens.spacing32};
        }

        :hover {
            background-color: ${tokens.shade200};
        }
    }

    ${NestedDropdown} & {
        align-items: center;

        ${StyledText} {
            ${text({size: 'sm', font: 'primary'})};

            ${mq.min(tabletMenuBreakpoint)} {
                ${text({size: 'xs', font: 'secondary'})};
                word-break: normal;
            }
        }
    }
`

export const DropdownItemBodyTextWrapper = styled.div`
    display: none;

    ${mq.min(tabletMenuBreakpoint)} {
        display: block;
    }
`

export const DropdownContentArea = styled.div<{columns: number}>`
    display: grid;
    gap: ${tokens.spacing20};
    order: 1;

    ${mq.min(tabletMenuBreakpoint)} {
        order: 2;
        grid-template-columns: repeat(${(props) => props.columns}, 1fr);
        grid-auto-rows: min-content;
        gap: 0 ${tokens.spacing20};
    }

    ${DisclosureWrapper}:nth-child(2) & {
        ${mq.max(tabletMenuBreakpoint)} {
            order: 2;
            gap: ${tokens.spacing20};
        }
    }
`

export const IconWrapper = styled(BackgroundWrapper)`
    display: none;
    width: 80px;
    height: 80px;
    padding: ${tokens.spacing14};
    border-radius: ${tokens.circle};

    ${mq.min(tabletMenuBreakpoint)} {
        display: block;

        img {
            width: initial;
        }
    }
`

export const WhyPleoDropdownContentColumn = styled.div<{$isExpanded: boolean}>`
    display: grid;
    grid-template-rows: auto 1fr;

    ${mq.max(tabletMenuBreakpoint)} {
        gap: ${(props) => (props.$isExpanded ? tokens.spacing20 : 0)};
    }

    ${mq.min(tabletMenuBreakpoint)} {
        padding-top: ${tokens.spacing10};
        gap: ${tokens.spacing6};

        ${Arrow} {
            display: none;
        }
    }
`

export const WhyPleoDropdownHeadingWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing6};
`

export const WhyPleoDropdownCategoryTitle = styled.p`
    ${text({size: 'sm', font: 'primary', color: 'muted'})}

    ${mq.max(tabletMenuBreakpoint)} {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: ${tokens.spacing10};
        align-items: center;
    }

    ${mq.min(tabletMenuBreakpoint)} {
        padding-left: ${tokens.spacing12};
        ${text({size: 'xs', font: 'secondary', color: 'muted'})}
    }

    ${mq.min('lg')} {
        padding-left: ${tokens.spacing20};
    }
`

export const WhyPleoDropdownCategorySentence = styled(WhyPleoDropdownCategoryTitle)`
    ${text({size: 'xxs', font: 'secondary', color: 'lightMuted'})};
`

export const CtaWrapper = styled.div<{$menuOpen: boolean}>(({$menuOpen}) => {
    const menuOpenStyles = css`
        ${mq.max(tabletMenuBreakpoint)} {
            position: fixed;
            bottom: 0;
            left: 0;
            display: grid;
            width: 100%;
            padding: ${tokens.spacing20} ${tokens.spacing24} ${tokens.spacing40};
            background: ${tokens.shade000};
        }

        ${mq.between(mobileMenuBreakpoint, tabletMenuBreakpoint)} {
            right: 0;
            left: unset;
            width: ${mobileMenuWidth};
            padding: ${tokens.spacing20} ${tokens.spacing40} ${tokens.spacing40};
            border-bottom-left-radius: ${tokens.spacing48};
        }
    `

    return css`
        ${$menuOpen && menuOpenStyles}

        ${mq.min(tabletMenuBreakpoint)} {
            padding-left: ${tokens.spacing12};
        }

        ${mq.min('lg')} {
            padding-left: ${tokens.spacing16};
        }
    `
})

export const StickyCtaWrapper = styled.div`
    display: block;
    padding: 0;
    animation: ${animations.fadeIn} ${tokens.gradual};
    ${mq.min(mobileMenuBreakpoint)} {
        display: none;
    }
`

export const Nav = styled.nav`
    display: none;

    ${mq.min(mobileMenuBreakpoint)} {
        display: grid;
        justify-content: flex-end;
        padding-right: ${tokens.spacing40};
    }

    ${mq.min(tabletMenuBreakpoint)} {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 100%;
        padding-right: unset;
    }
`

const tabletNavStyles = css`
    ${NavGroupLeft} {
        display: none;
    }

    ${NavGroupRight} {
        display: inline-grid;
        padding-bottom: initial;
        grid-auto-flow: column;
        grid-template-rows: auto;
        gap: ${tokens.spacing40};
    }

    ${NavLink} {
        font-size: ${tokens.fontLarge};
        grid-template-columns: auto;
        gap: ${tokens.spacing4};
    }

    ${CtaWrapper} {
        display: grid;
    }

    ${NavCta} {
        ${textLinkStyle}
        background: none;
        border: none;
        border-radius: 0;

        ${mq.max(tabletMenuBreakpoint)} {
            font-family: ${fonts.families.secondary};
            font-size: ${tokens.fontLarge};
        }

        ${mq.between(mobileMenuBreakpoint, tabletMenuBreakpoint)} {
            text-decoration: underline;
        }

        :hover {
            background: ${colors.background.pink};
        }
    }
`

const mobileMenuStyles = css`
    position: fixed;
    top: 0;
    bottom: 0;
    align-items: flex-start;

    ${mq.max(tabletMenuBreakpoint)} {
        padding-top: ${tokens.spacing60};
    }

    ${MobileLogo} {
        display: none;
    }

    ${Nav} {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
        gap: ${tokens.spacing40};
        height: 100%;
        padding-top: 100px;

        ${mq.min(tabletMenuBreakpoint)} {
            padding-top: initial;
        }
    }

    ${NavGroup} {
        width: 100%;
        flex-direction: column;
        align-items: flex-start;

        a:hover {
            text-decoration: none;
        }
    }

    ${NavGroupRight} a:not(${NavCta}) {
        ${text({size: 'sm', font: 'primary'})}
    }
`

export const HeaderInner = styled.div<WrapperProps>`
    ${container}
    display: grid;
    width: 100%;
    padding-right: 0;
    padding-left: 0;
    margin: 0 auto;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: ${(props) => (props.$menuOpen ? 'start' : 'center')};

    ${mq.between(mobileMenuBreakpoint, tabletMenuBreakpoint)} {
        grid-template-columns: ${(props) => (props.$menuOpen ? 'auto' : 'auto 1fr')};
    }

    ${mq.min(tabletMenuBreakpoint)} {
        grid-template-columns: auto 1fr;
        align-items: center;
        padding-right: 0;
        padding-left: 0;
    }
`

// Adding fixed height from theme to accurately position
// elements around header / calculate scroll offsets
export const HeaderWrapper = styled(RemoveScroll)<WrapperProps>(({$menuOpen}) => {
    return css`
        position: sticky;
        top: 0;
        left: 0;
        z-index: ${zIndex.header};
        display: flex;
        width: 100%;
        padding: ${tokens.spacing16} ${tokens.spacing24};
        padding-bottom: ${$menuOpen && '0'};
        background: ${colors.background.default};
        align-items: center;

        ${mq.between(mobileMenuBreakpoint, tabletMenuBreakpoint)} {
            right: 0;
            left: unset;
            display: grid;
            width: ${$menuOpen ? mobileMenuWidth : '100%'};
            align-items: flex-end;
            grid-auto-flow: column;
            grid-template-columns: auto;
            border-bottom-left-radius: ${$menuOpen ? tokens.spacing48 : 0};

            ${!$menuOpen && tabletNavStyles}
        }

        ${mq.min(mobileMenuBreakpoint)} {
            padding-right: ${tokens.spacing40};
            padding-left: ${tokens.spacing40};
        }

        ${mq.max(headerSizes.breakpoint)} {
            overflow: auto;

            & > * + * {
                margin-left: 0;
            }

            ${MobileMenuBtn} {
                display: block;
            }

            ${DesktopLogo} {
                display: none;
            }

            ${MobileLogo} {
                display: block;
            }

            ${$menuOpen && mobileMenuStyles}
        }

        ${mq.min(headerSizes.breakpoint)} {
            height: ${headerSizes.heightLarge}px;
        }
    `
})
